import { Either, Schema } from "effect";

// Wire contracts from Credit Key's V2 Merchant API, reviewed 2026-09-06.
// All amounts remain USD dollars. These contracts do not grant payment authority.
const Text = Schema.String.pipe(Schema.filter((value) => value.trim().length > 0));
export const CreditKeyDollars = Schema.Number.pipe(
	Schema.filter((value) => Number.isFinite(value) && value >= 0 && value <= 1_000_000 && Number(value.toFixed(2)) === value),
);
const PositiveDollars = CreditKeyDollars.pipe(Schema.filter((value) => value > 0));
const Quantity = Schema.Number.pipe(Schema.int(), Schema.between(1, 100_000));

export const CreditKeyAddressSchema = Schema.Struct({
	address1: Text,
	address2: Schema.optional(Schema.String),
	city: Text,
	state: Text,
	zip: Text,
	first_name: Text,
	last_name: Text,
	company_name: Schema.optional(Text),
	email: Text,
	phone_number: Text,
});

export const CreditKeyItemSchema = Schema.Struct({
	merchant_id: Schema.optional(Text),
	name: Text,
	price: CreditKeyDollars,
	quantity: Quantity,
	sku: Schema.optional(Text),
});

export const CreditKeyCheckoutSchema = Schema.Struct({
	mode: Schema.Literal("REDIRECT", "MODAL"),
	merchant: Schema.Struct({
		cancel_url: Text,
		return_url: Text,
		order_id: Text,
		remote_id: Text,
		remote_customer_id: Schema.optional(Text),
	}),
	billing_address: CreditKeyAddressSchema,
	shipping_address: CreditKeyAddressSchema,
	metadata: Schema.optional(Schema.Struct({
		fulfillment_method: Schema.Literal("pickup"),
		customer_hold_days: Schema.optional(Schema.Number.pipe(Schema.filter((days) => [15, 30, 45, 60, 90].includes(days)))),
	})),
	amount: PositiveDollars,
	charges: Schema.Struct({
		subtotal: CreditKeyDollars,
		discount_amount: Schema.Number.pipe(Schema.filter((value) => Number.isFinite(value) && value <= 0 && Number(value.toFixed(2)) === value)),
		shipping: CreditKeyDollars,
		tax: CreditKeyDollars,
	}),
	items: Schema.Array(CreditKeyItemSchema).pipe(Schema.minItems(1)),
}).pipe(Schema.filter((order) => {
	const subtotal = Number(order.items.reduce((sum, item) => sum + item.price * item.quantity, 0).toFixed(2));
	const total = Number((subtotal + order.charges.discount_amount + order.charges.shipping + order.charges.tax).toFixed(2));
	return subtotal === order.charges.subtotal && total === order.amount;
}));

export type CreditKeyCheckout = typeof CreditKeyCheckoutSchema.Type;
export type CreditKeyAddress = typeof CreditKeyAddressSchema.Type;

// V2 /hosted/status is a merchant-level availability check, not borrower approval.
export const CreditKeyHostedStatusSchema = Schema.Struct({
	allowed: Schema.Boolean,
});

export const CreditKeyHostedSessionSchema = Schema.Struct({
	key: Text,
	url: Text,
	mode: Schema.Literal("REDIRECT", "MODAL"),
});

export const CreditKeyOrderSchema = Schema.Struct({
	key: Text,
	amount: CreditKeyDollars,
	amount_refunded: CreditKeyDollars,
	status: Schema.Literal("CREATED", "AUTHORIZED", "CAPTURED", "CANCELED", "RETURNED", "PENDING", "FAILED"),
	merchant_order_id: Schema.optional(Text),
}).pipe(Schema.filter((order) => order.amount_refunded <= order.amount &&
	(order.status !== "RETURNED" || order.amount_refunded === order.amount)));
export type CreditKeyOrder = typeof CreditKeyOrderSchema.Type;

// Only the fields required for an authenticated, read-only credit check survive
// decoding. Borrower data stays server-side and is never payment authority.
export const CreditKeyCompanySchema = Schema.Struct({
	id: Text,
	status: Schema.Literal("APPROVED", "PENDING", "DECLINED", "HOLD", "UNKNOWN", "NEW", "SUSPENDED"),
	tcl_amount_remaining: CreditKeyDollars,
	ordering_available: Schema.Boolean,
	remote_customer_id: Schema.optional(Schema.NullOr(Schema.String)),
	borrower: Schema.Struct({ email: Text }),
});
export const CreditKeyCompanySearchSchema = Schema.Struct({
	data: Schema.Array(CreditKeyCompanySchema),
	metadata: Schema.Struct({ total_pages: Schema.Number.pipe(Schema.int(), Schema.nonNegative()) }),
});

// This is Anthem's integration-lab model, NOT a Credit Key underwriting response.
export const CreditKeyLabOrderSchema = Schema.Struct({
	unitPrice: PositiveDollars,
	quantity: Quantity,
	shipNow: Schema.Number.pipe(Schema.int(), Schema.between(0, 100_000)),
}).pipe(Schema.filter((order) => order.shipNow <= order.quantity));
export type CreditKeyLabOrder = typeof CreditKeyLabOrderSchema.Type;

export function decodeCreditKeyLabOrder(value: unknown): CreditKeyLabOrder {
	const result = Schema.decodeUnknownEither(CreditKeyLabOrderSchema)(value);
	if (Either.isLeft(result)) throw new Error("Enter a positive unit price and whole bag quantities. Ship-now bags cannot exceed purchased bags.");
	return result.right;
}
